import React from 'react';
import { StyleSheet } from 'react-native';
import Animated, { LinearTransition, withSpring, withTiming } from 'react-native-reanimated';

import { AvatarGroup } from '@/assets/icons/avatarGroup';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PlayerAvatar } from '@/components/player-avatar/PlayerAvatar';
import { usePreviousBetslip } from '@/feature/betslip-pickem/hooks/use-betslip-data';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import { designSystem } from '@/styles/styles';
import { getEnteringAnimation, getExitingAnimation } from '@/utils/animations/layout-animations-utils';
import { isWeb } from '@/utils/constants-platform-specific';

import { MAX_HEADSHOTS } from './FantasyPickbar';

export const PLAYER_SIZE = 40;

export const PlayersHeadshotsList = ({ hasRendered }: { hasRendered: boolean }) => {
    const betslip = useBetslipStore(state => state.betslip);
    const { picks: previousPicks } = usePreviousBetslip();
    // On first render (1 pick), use betslip directly so the headshot shows immediately
    // without waiting for validation. In all other cases, use previousPicks.
    const picks = betslip.length === 1 ? betslip : previousPicks;
    const noPicks = picks.length === 0;
    const displayedPicks = picks.slice(-MAX_HEADSHOTS);
    const reversedPicks = displayedPicks.toReversed();
    const shouldUseSimpleAnimation = picks.length >= MAX_HEADSHOTS;
    const isFirstPick = picks.length === 1;
    // Skip entering animations for the first pick so the headshot appears instantly
    // as the pickbar slides in, rather than animating separately.
    const shouldAnimateEntering = hasRendered && !isFirstPick;

    return (
        <Animated.View style={styles.headshotsWrapper} testID={'players-headshots-list'}>
            {noPicks ? (
                <Animated.View
                    style={[styles.headshotsGroup, isWeb ? {} : styles.shadow]}
                    entering={getEnteringAnimation(scaleEnteringAnimation, shouldAnimateEntering)}
                    exiting={getExitingAnimation(scaleExitingAnimation)}
                >
                    <AvatarGroup width={36} height={36} />
                </Animated.View>
            ) : null}
            {!noPicks ? (
                <Animated.View
                    style={styles.headshotsContainer}
                    entering={getEnteringAnimation(scaleEnteringAnimation, shouldAnimateEntering)}
                    exiting={getExitingAnimation(scaleExitingAnimation)}
                >
                    {reversedPicks.map((pick, index) => {
                        const useSimpleHeadshotEntering = shouldUseSimpleAnimation && index > 0;
                        const playerHeadshotEnteringAnimation = useSimpleHeadshotEntering
                            ? simpleHeadshotEnteringAnimation
                            : headshotEnteringAnimation;
                        return (
                            <Animated.View
                                key={createPlayerEventKey(pick.player.id, pick.eventId)}
                                style={[
                                    styles.headshotContainer,
                                    index > 0 && styles.overlappingHeadshot,
                                    styles.shadow,
                                    { zIndex: reversedPicks.length - index },
                                ]}
                                entering={getEnteringAnimation(playerHeadshotEnteringAnimation, shouldAnimateEntering)}
                                exiting={getExitingAnimation(headshotExitingAnimation)}
                                layout={LinearTransition.duration(100)}
                            >
                                {pick ? (
                                    <Box style={styles.imageContainer}>
                                        <PlayerAvatar playerData={pick.player} playerAvatarVariant={'headshot'} />
                                    </Box>
                                ) : null}
                            </Animated.View>
                        );
                    })}
                    {picks.length > MAX_HEADSHOTS && (
                        <Animated.View
                            style={styles.moreIndicator}
                            entering={getEnteringAnimation(moreIndicatorEnteringAnimation, hasRendered)}
                            exiting={getExitingAnimation(moreIndicatorExitingAnimation)}
                        >
                            <Text variant="labelLarge2" color={ds.colorToken.text.primary} lineHeight={24}>
                                +{picks.length - MAX_HEADSHOTS}
                            </Text>
                        </Animated.View>
                    )}
                </Animated.View>
            ) : null}
        </Animated.View>
    );
};

// Animation functions for headshots components
const scaleEnteringAnimation = () => {
    'worklet';
    return {
        initialValues: {
            opacity: 0,
            transform: [{ scale: 0.8 }],
        },
        animations: {
            opacity: withTiming(1, { duration: 200 }),
            transform: [{ scale: withTiming(1, { duration: 200 }) }],
        },
    };
};

const scaleExitingAnimation = () => {
    'worklet';
    return {
        initialValues: {
            opacity: 1,
            transform: [{ scale: 1 }],
        },
        animations: {
            opacity: withTiming(0, { duration: 150 }),
            transform: [{ scale: withTiming(0.8, { duration: 150 }) }],
        },
    };
};
const headshotEnteringAnimation = () => {
    'worklet';
    return {
        initialValues: {
            opacity: 0,
            transform: [{ scale: 0.5 }, { translateX: -30 }],
        },
        animations: {
            opacity: withTiming(1, { duration: 250 }),
            transform: [
                { scale: withSpring(1, { damping: 25, stiffness: 300 }) },
                { translateX: withSpring(0, { damping: 25, stiffness: 300 }) },
            ],
        },
    };
};

// Simple entering animation for when more indicator is shown (no spring effect)
const simpleHeadshotEnteringAnimation = () => {
    'worklet';
    return {
        initialValues: {
            opacity: 0,
            transform: [{ scale: 0.8 }, { translateX: 20 }],
        },
        animations: {
            opacity: withTiming(1, { duration: 200 }),
            transform: [{ scale: withTiming(1, { duration: 200 }) }, { translateX: withTiming(0, { duration: 200 }) }],
        },
    };
};

// Enhanced exiting animation - slides to the left
const headshotExitingAnimation = () => {
    'worklet';
    return {
        initialValues: {
            opacity: 1,
            transform: [{ translateX: 0 }],
        },
        animations: {
            opacity: withTiming(0, { duration: 150 }),
            transform: [{ translateX: withTiming(-35, { duration: 150 }) }],
        },
    };
};

// Enhanced more indicator animations with smooth scaling
const moreIndicatorEnteringAnimation = () => {
    'worklet';
    return {
        initialValues: {
            opacity: 0,
            transform: [{ scale: 0.3 }],
        },
        animations: {
            opacity: withTiming(1, { duration: 200 }),
            transform: [{ scale: withSpring(1, { damping: 15, stiffness: 200 }) }],
        },
    };
};

const moreIndicatorExitingAnimation = () => {
    'worklet';
    return {
        initialValues: {
            opacity: 1,
            transform: [{ scale: 1 }],
        },
        animations: {
            opacity: withTiming(0, { duration: 150 }),
            transform: [{ scale: withTiming(0.3, { duration: 150 }) }],
        },
    };
};

const styles = StyleSheet.create({
    headshotsWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    headshotsContainer: {
        marginRight: 16,
        flexDirection: 'row',
        alignItems: 'center',
    },
    headshotsGroup: {
        marginRight: 12,
        marginLeft: 8,
    },
    headshotContainer: {
        width: 40,
        height: 40,
        borderRadius: 28,
        backgroundColor: ds.colorValue.background.neutral.stronger,
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
    shadow: {
        shadowOffset: {
            height: 0,
            width: 4,
        },
        shadowRadius: 4,
        shadowOpacity: 0.5,
        shadowColor: designSystem.colors.neutral600,
        elevation: 4,
    },
    overlappingHeadshot: {
        marginLeft: -8,
    },
    moreIndicator: {
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 6,
    },
    imageContainer: {
        width: PLAYER_SIZE,
        height: PLAYER_SIZE,
        borderRadius: 28,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
